/**
 * RDS Spacing 토큰 (px, 4 그리드).
 * Figma `Spacing/*`에 명시된 값(12·16·20·24·40) + 동일 스케일 확장.
 * Tailwind 기본 spacing(0.25rem=4px)과 1:1 대응 → `gap-1`=4 … `gap-10`=40.
 *
 * @see Figma Spacing variables (Radius 등 프레임에서 참조)
 */
export const rdsSpacing = {
  0: 0,
  4: 4,
  8: 8,
  /** Figma Spacing/12 */
  12: 12,
  /** Figma Spacing/16 */
  16: 16,
  /** Figma Spacing/20 */
  20: 20,
  /** Figma Spacing/24 */
  24: 24,
  32: 32,
  /** Figma Spacing/40 */
  40: 40,
  48: 48,
  56: 56,
  64: 64,
  80: 80,
} as const;

export type RdsSpacing = keyof typeof rdsSpacing;

/**
 * RDS px → Tailwind gap/space 유틸 클래스 suffix.
 * gap-{n} where n * 4px = token (기본 --spacing 0.25rem).
 */
export const rdsSpacingToTailwind = {
  0: '0',
  4: '1',
  8: '2',
  12: '3',
  16: '4',
  20: '5',
  24: '6',
  32: '8',
  40: '10',
  48: '12',
  56: '14',
  64: '16',
  80: '20',
} as const satisfies Record<RdsSpacing, string>;

/**
 * RDS px → Tailwind gap 유틸 전체 클래스.
 * 동적 문자열(`gap-${n}`)은 Tailwind가 스캔하지 못하므로 정적 맵을 쓴다.
 */
export const rdsGapClassNameMap = {
  0: 'gap-0',
  4: 'gap-1',
  8: 'gap-2',
  12: 'gap-3',
  16: 'gap-4',
  20: 'gap-5',
  24: 'gap-6',
  32: 'gap-8',
  40: 'gap-10',
  48: 'gap-12',
  56: 'gap-14',
  64: 'gap-16',
  80: 'gap-20',
} as const satisfies Record<RdsSpacing, string>;

export const rdsGapClassName = (space: RdsSpacing): string =>
  rdsGapClassNameMap[space];

export const rdsSpaceYClassNameMap = {
  0: 'space-y-0',
  4: 'space-y-1',
  8: 'space-y-2',
  12: 'space-y-3',
  16: 'space-y-4',
  20: 'space-y-5',
  24: 'space-y-6',
  32: 'space-y-8',
  40: 'space-y-10',
  48: 'space-y-12',
  56: 'space-y-14',
  64: 'space-y-16',
  80: 'space-y-20',
} as const satisfies Record<RdsSpacing, string>;

export const rdsSpaceYClassName = (space: RdsSpacing): string =>
  rdsSpaceYClassNameMap[space];

export const rdsSpaceXClassNameMap = {
  0: 'space-x-0',
  4: 'space-x-1',
  8: 'space-x-2',
  12: 'space-x-3',
  16: 'space-x-4',
  20: 'space-x-5',
  24: 'space-x-6',
  32: 'space-x-8',
  40: 'space-x-10',
  48: 'space-x-12',
  56: 'space-x-14',
  64: 'space-x-16',
  80: 'space-x-20',
} as const satisfies Record<RdsSpacing, string>;

export const rdsSpaceXClassName = (space: RdsSpacing): string =>
  rdsSpaceXClassNameMap[space];

export const toSpacingCss = (px: RdsSpacing): string => `${rdsSpacing[px]}px`;
